import { useState, useMemo, FC, useEffect } from 'react';
import { Link as _Link, useParams, useSearchParams } from 'react-router-dom';
import {
  FormControl as _FormControl,
  FormControlLabel,
  Grid,
  Select,
  MenuItem as _MenuItem,
  InputLabel,
  Slider as _Slider,
  Switch,
  Tabs as _Tabs,
  Tab as _Tab,
} from '@material-ui/core';
import OpenInNew from '@material-ui/icons/OpenInNew';
import { styled } from '@material-ui/styles';
import { useQuery, useReactiveVar, gql } from '@apollo/client';

import { PageTitle, SectionSubTitleStyle, BodySmall } from '../component/primitive/TypographyUI';
import Button from '../component/primitive/Button';
import { COLOR_TEXT_MAIN } from '../../theme/colors';
import { SPACING_MEDIUM } from '../constant/spacing';
import { lastAccessedOrganizationVar } from '../../cache';
import { Clickmap, Scrollmap, Histmap } from '../component/compound/Sitedock';
import {
  GetProjectDocument,
  GetProjectQuery,
  GetProjectQueryVariables,
  ChartBreakdown,
  Chart,
  PossibleVariation,
  RoundOfProject,
} from '../../generated/graphql';

const GetBreakdownDocument = gql`
  query GetBreakdown(
    $id: ID!
    $identifierType: String!
    $scope: ChartScopeInput!
    $breakdowns: [ChartBreakdownInput!]
  ) {
    chart(id: $id, identifierType: $identifierType, scope: $scope, breakdowns: $breakdowns) {
      breakdowns {
        key {
          label
          value
        }
        display
        filterRequired
        values
      }
    }
  }
`;

const languageMap = new Map([
  ['initial', '準備中'],
  ['competition', 'ラウンド'],
  ['interim', 'インターバル'],
]);

const SitedockPage: FC = () => {
  const org_id = useReactiveVar(lastAccessedOrganizationVar)?.id;
  const { project_id } = useParams<{ project_id: string }>();
  const [searchParams] = useSearchParams();
  const devMode = searchParams.get('dev') ?? false;
  const [variations, setVariations] = useState<PossibleVariation[]>();

  const {
    loading: projectLoading,
    error: projectError,
    data: { project: { rounds } } = { project: { rounds: null } },
  } = useQuery<GetProjectQuery, GetProjectQueryVariables>(GetProjectDocument, {
    skip: !project_id,
    variables: { id: project_id! },
    context: { clientName: 'planbcd' },
  });

  const {
    loading: dataplatformLoading,
    error: breakdownError,
    data: { chart: { breakdowns } } = {
      chart: { breakdowns: null },
    },
  } = useQuery<{ chart: Chart }>(GetBreakdownDocument, {
    variables: {
      id: `planbcd_clickmap_${org_id}`,
      identifierType: 'resource_key',
      scope: { from: '2021-01-01', to: '2022-01-31' },
    },
    context: { clientName: 'dataplatform' },
    skip: !rounds,
  });

  const [round, setRound] = useState<string>('');
  const [segment, setSegment] = useState<string>('');
  const [segments, setSegments] = useState<string[]>(['']);
  const [hideMap, setHideMap] = useState(false);
  const [isGreenMode, setGreenMode] = useState(false);
  const [dataLimit, setDataLimit] = useState<number>();
  const [tab, setTab] = useState(0);

  const onChangeCommittedDataLimit = (_: any, value: number | number[]) =>
    Array.isArray(value) ? setDataLimit(value[0]) : setDataLimit(value);

  useEffect(() => {
    if (rounds) {
      setRound(rounds![0].id);
      setVariations(rounds![0].variations);
    }
  }, [rounds]);

  useEffect(() => {
    if (round) {
      const r: RoundOfProject = rounds!.filter((r) => r.id === round)[0];
      setVariations(r.variations);
    }
  }, [round, rounds]);

  useEffect(() => {
    if (breakdowns)
      setSegments(
        (breakdowns as NonNullable<ChartBreakdown>[])
          .filter(({ key }: Pick<ChartBreakdown, 'key'>): boolean => key.value.includes('segment'))
          .flatMap(({ values }: Pick<ChartBreakdown, 'values'>): string[] => values ?? ['']),
        // FEATURE: return all combinations of n arrays: Cartesian product
        //.map(({ values }: Pick<ChartBreakdown, 'values'>): string[] => (values ? values : ['']))
        //.reduce((a: string[], b: string[]) => a.flatMap((d: string) => b.map((e: string) => `${d}-${e}`))),
      );
  }, [breakdowns]);

  const handleRoundChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setRound(`${event.target.value}`);
  };
  const handleSegmentChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setSegment(`${event.target.value}`);
  };
  const toggleHideMap = () => setHideMap(!hideMap);
  const toggleGreenMode = () => setGreenMode(!isGreenMode);

  const handleTab = (_ = {} /*event not needed TS6133*/, newValue: number) => setTab(newValue);

  const SelectedSitedockMap = useMemo(() => {
    if (!round || !variations) return null;
    switch (tab) {
      default:
      case 0:
        return (
          <FlexMapsContainer>
            {variations.map(({ id: variation_id, original_screenshot_image: src }: PossibleVariation, i: number) => (
              <Clickmap
                org_id={org_id!}
                project_id={project_id!}
                round_id={round}
                variation_id={variation_id}
                segment={segment}
                src={src}
                hide={hideMap}
                isGreenMode={isGreenMode}
                dataLimit={dataLimit}
                key={i}
              />
            ))}
            {variations.length <= 2 && [...Array(3 - variations.length).keys()].map((n) => <List key={n}></List>)}
          </FlexMapsContainer>
        );
      case 1:
        return (
          <FlexMapsContainer>
            {variations.map(({ id: variation_id, original_screenshot_image: src }: PossibleVariation, i: number) => (
              <Scrollmap
                org_id={org_id!}
                project_id={project_id!}
                round_id={round}
                variation_id={variation_id}
                segment={segment}
                src={src}
                hide={hideMap}
                dataLimit={dataLimit}
                key={i}
              />
            ))}
            {variations.length <= 2 && [...Array(3 - variations.length).keys()].map((n) => <List key={n}></List>)}
          </FlexMapsContainer>
        );
      case 2:
        return (
          <FlexMapsContainer>
            {variations.map(({ id: variation_id, original_screenshot_image: src }: PossibleVariation, i: number) => (
              <Histmap
                org_id={org_id!}
                project_id={project_id!}
                round_id={round}
                variation_id={variation_id}
                segment={segment}
                src={src}
                hide={hideMap}
                dataLimit={dataLimit}
                key={i}
              />
            ))}
            {variations.length <= 2 && [...Array(3 - variations.length).keys()].map((n) => <List key={n}></List>)}
          </FlexMapsContainer>
        );
    }
  }, [project_id, round, variations, segment, hideMap, tab, dataLimit]);

  const RoundItems = useMemo(() => {
    if (!rounds) return;
    return [...rounds!]
      .filter(({ type }) => type !== 'archived')
      .sort(({ id: first }: RoundOfProject, { id: second }: RoundOfProject) => {
        return Number(first) >= Number(second) ? 1 : -1;
      })
      .map(({ id, type, num }: RoundOfProject, i: number) => (
        <MenuItem value={id ?? ''} key={i}>
          {languageMap.get(type)} #{num}
        </MenuItem>
      ));
  }, [rounds]);

  const SegmentItems = useMemo(() => {
    return segments.map((segment: string, i: number) => (
      <MenuItem value={segment} key={i}>
        {segment}
      </MenuItem>
    ));
  }, [segments]);

  const Head = useMemo(
    () => (
      <TitleFlex>
        <PageTitle variant="h2">サイト診断: Project #{project_id}</PageTitle>
        <Button variant="contained" size="small">
          <_Link to="/engine/segment">セグメント情報</_Link>
        </Button>
      </TitleFlex>
    ),
    [project_id],
  );

  if (projectLoading || dataplatformLoading)
    return (
      <Root>
        {Head}
        <Container>Loading...</Container>
      </Root>
    );
  if (projectError)
    return (
      <Root>
        {Head}
        <Container>Project Error: {projectError?.message}</Container>
      </Root>
    );
  if (breakdownError)
    return (
      <Root>
        {Head}
        <Container>
          ご利用にはお手続きが必要です。
          <a target="_blank" rel="noreferrer" href="https://support.kaizenplatform.net/hc/ja/requests/new">
            こちらからお問い合わせください
            <OpenInNew fontSize="inherit" />
          </a>
        </Container>
      </Root>
    );
  if (!org_id)
    return (
      <Root>
        {Head}
        <Container>cannot find organization</Container>
      </Root>
    );
  if (!variations)
    return (
      <Root>
        {Head}
        <Container>cannot find variations</Container>
      </Root>
    );

  return (
    <Root>
      {Head}
      <Tabs value={tab} onChange={handleTab} aria-label="Sitedock tabs" indicatorColor="primary" textColor="inherit">
        <Tab label="クリックマップ" />
        <Tab label="スクロールマップ" />
        <Tab label="離脱マップ" />
      </Tabs>
      <Container>
        <FormControl variant="outlined" size="small">
          <InputLabel id="Round-select-label">ラウンド</InputLabel>
          <Select
            labelId="Round-select-label"
            id="Round-select"
            value={round}
            label="ラウンド"
            onChange={handleRoundChange}
            style={{
              width: 220,
              textTransform: 'capitalize',
            }}
          >
            <MenuItem value="" key={99}>
              -
            </MenuItem>
            {RoundItems}
          </Select>
        </FormControl>
        <FormControl variant="outlined" size="small">
          <InputLabel id="segment-select-label">セグメント</InputLabel>
          <Select
            labelId="segment-select-label"
            id="segment-select"
            value={segment}
            label="セグメント"
            onChange={handleSegmentChange}
            style={{
              width: 200,
            }}
          >
            <MenuItem value="" key={99}>
              全ユーザー
            </MenuItem>
            {SegmentItems}
          </Select>
        </FormControl>
        <FormControlLabel
          label="Hide map"
          control={
            <Switch
              checked={hideMap}
              onChange={toggleHideMap}
              color="primary"
              name="hidemap"
              inputProps={{ 'aria-label': 'primary checkbox' }}
            />
          }
        />
        {devMode !== false && (
          <>
            <FormControlLabel
              label="Green Mode"
              control={
                <Switch
                  checked={isGreenMode}
                  onChange={toggleGreenMode}
                  color="primary"
                  name="hidemap"
                  inputProps={{ 'aria-label': 'primary checkbox' }}
                />
              }
            />
            <SliderWrap item container direction="column">
              <BodySmall>Data量</BodySmall>
              <Slider
                defaultValue={100}
                onChangeCommitted={onChangeCommittedDataLimit}
                getAriaValueText={(v) => `${v != 1 ? v - 1 : v}%`}
                valueLabelFormat={(v) => `${1 < v && v < 100 ? v - 1 : v}%`}
                valueLabelDisplay="auto"
                marks
                step={10}
                min={1}
                max={100}
              />
            </SliderWrap>
          </>
        )}
        {SelectedSitedockMap}
      </Container>
    </Root>
  );
};

const Root = styled('div')({
  padding: '0 24px',
});

const Container = styled('div')({
  padding: SPACING_MEDIUM,
  backgroundColor: 'rgba(255, 255, 255, 1)',
  boxShadow: '0px 1px 9px rgba(0, 0, 0, 0.04)',
  borderRadius: '8px',
});

const FlexMapsContainer = styled('div')({
  display: 'flex',
  margin: SPACING_MEDIUM,
});

const TitleFlex = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
});

const Tabs = styled(_Tabs)({
  marginBottom: SPACING_MEDIUM,
  '& .MuiTabs-indicator': {
    backgroundColor: COLOR_TEXT_MAIN,
    height: '3px',
    borderRadius: '999px',
  },
});
const Tab = styled(_Tab)({
  ...SectionSubTitleStyle,
  ...{ '&.MuiTab-root': { padding: 0 } },
});

const List = styled('div')({
  margin: '20px 16px',
  flex: '1 0 160px',
  justifyContent: 'space-between',
});

const MenuItem = styled(_MenuItem)({
  textTransform: 'capitalize',
});

const FormControl = styled(_FormControl)({
  margin: '10px 20px',
});

const SliderWrap = styled(Grid)({ flex: '0 1 12rem' });
const Slider = styled(_Slider)({ width: 'auto' });

export default SitedockPage;
